import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Modal,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, Close } from '@mui/icons-material';
import { apiRequestJson } from '../../api/apiService';

/**
 * Componente para reset de senha via token.
 * Verifica validade do token, permite alteração de senha e realiza login automático.
 * Após alterar a senha com sucesso, o backend invalida sessões antigas e cria nova sessão.
 * Modal em tela cheia seguindo o tema da aplicação.
 *
 * @component
 * @returns {JSX.Element} Modal de reset de senha
 */
export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /**
   * Verifica validade do token na inicialização
   */
  useEffect(() => {
    const verifyToken = async () => {
      try {
        setLoading(true);
        const response = await apiRequestJson('users_password_verify_token', {
          params: { token }
        });

        if (response.success) {
          setTokenValid(true);
          setTokenData(response.data);
        } else {
          setTokenValid(false);
          setError(response.message || 'Token inválido ou expirado');
        }
      } catch (err) {
        setTokenValid(false);
        setError('Erro ao verificar token');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setLoading(false);
      setError('Token não fornecido');
    }
  }, [token]);

  /**
   * Manipula mudanças nos campos do formulário
   */
  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError('');
  };

  /**
   * Valida e submete nova senha
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      
      const response = await apiRequestJson('users_password_update', {
        method: 'PUT',
        body: {
          token,
          newPassword: formData.newPassword
        }
      });

      if (response.success) {
        // Armazenar dados do usuário no localStorage (sessão criada automaticamente)
        if (response.data?.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }
        
        setSuccess('Senha alterada com sucesso! Login automático realizado. Redirecionando...');
        setTimeout(() => {
          navigate('/');
          window.location.reload(); // Recarregar para atualizar estado da aplicação
        }, 2000);
      } else {
        setError(response.message || 'Erro ao alterar senha');
      }
    } catch (err) {
      setError('Erro ao alterar senha');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Fecha modal e volta para página inicial
   */
  const handleClose = () => {
    navigate('/');
  };

  return (
    <Modal
      open={true}
      onClose={handleClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      <Paper
        sx={{
          width: '100%',
          maxWidth: 500,
          maxHeight: '90vh',
          overflow: 'auto',
          p: 4,
          position: 'relative'
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8
          }}
        >
          <Close />
        </IconButton>

        <Typography variant="h4" component="h1" gutterBottom align="center">
          Alteração de Senha
        </Typography>

        {loading && (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !tokenValid && (
          <Box>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error || 'Não existe requisição para alteração de senha ou requisição expirada'}
              {tokenData?.expires_at && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Expirou em: {new Date(tokenData.expires_at).toLocaleString('pt-BR')}
                </Typography>
              )}
            </Alert>
            <Button
              variant="contained"
              fullWidth
              onClick={handleClose}
              sx={{ mt: 2 }}
            >
              Voltar
            </Button>
          </Box>
        )}

        {!loading && tokenValid && (
          <Box component="form" onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Nova Senha"
              type={showPassword ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={handleInputChange('newPassword')}
              required
              margin="normal"
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                )
              }}
            />

            <TextField
              fullWidth
              label="Repita Nova Senha"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleInputChange('confirmPassword')}
              required
              margin="normal"
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                )
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={submitting}
              sx={{ mt: 3, mb: 2 }}
            >
              {submitting ? <CircularProgress size={24} /> : 'Alterar'}
            </Button>
          </Box>
        )}
      </Paper>
    </Modal>
  );
}